import React from 'react';
import { FiInfo } from 'react-icons/fi';
import { DashboardHeader } from '../../core/DashboardHeader';
import { DashboardWrapper } from '../../core/DashboardWrapper';
import { CategoryCard } from '../../core/reusable-forms/CategoryCard';
import { FeatureImageCard } from '../../core/reusable-forms/FeatureImageCard';
import { IntroductionVideoCard } from '../../core/reusable-forms/IntroductionVideoCard';
import { PublishingStatusCard } from '../../core/reusable-forms/PublishingStatusCard';
import { TagsCard } from '../../core/reusable-forms/TagsCard';
import { TitleAndDescriptionCard } from '../../core/reusable-forms/TitleAndDescriptionCard';
import { RhButton, RhButtonMode } from '../../core/RhButton';
import { RhFormGrid, RhFormGridColumn } from '../../core/RhFormGrid';
import { RhMoreDropdown } from '../../core/RhMoreDropdown';
import { H4 } from '../../core/Styles';
import { BibleStudyFormProps } from '../containers/BibleStudyFormContainer';
import { GuidedContentCard } from './shared/GuidedContentCard';
import { GuidedContentCreationModal } from './shared/GuidedContentCreationModal';
import { JiralikeContentModal } from './shared/JiralikeContentModal';
import { ModuleModal } from './shared/ModuleModal';
import { StudySectionCards } from './shared/StudySectionCards';

export const BibleStudyFormScreen: React.FC<BibleStudyFormProps> = props => {
  return (
    <DashboardWrapper>
      <DashboardHeader
        title={
          props.mode === 'add' ? (
            <div className="flex items-center space-x-1">
              <FiInfo className="text-yellow-400" />
              <H4>Auto-save gets triggered when you add or remove a section</H4>
            </div>
          ) : (
            ''
          )
        }
        actions={[
          <RhButton key="bsp-add-cancel" title="Cancel" mode={RhButtonMode.text} onClick={props.userClickedCancel} />,
          !!props.userClickedResetForm && (
            <RhButton
              key="bsp-reset"
              title="Reset Form"
              loading={props.isSaving}
              onClick={props.userClickedResetForm}
              disabled={props.isSaving}
              mode={RhButtonMode.hollow}
            />
          ),
          <RhButton
            key="bsp-add-save"
            title="Save Changes"
            loading={props.isSaving}
            onClick={props.userClickedSave}
            disabled={!props.form.title || !props.form.description || props.isSaving}
          />,
          <RhMoreDropdown
            key="more-dropdown"
            isDeleteDisabled={props.isSaving}
            deletePermission="delete:programs"
            userClickedDelete={() => {
              props.userClickedDeleteStudy?.();
            }}
          />,
        ]}
      />
      <RhFormGrid>
        <RhFormGridColumn>
          {/* Title and Description  */}
          <TitleAndDescriptionCard
            form={props.form}
            isEditingTitle={props.isEditingTitle}
            userClickedEditTitle={props.userClickedEditTitle}
            userUpdatedForm={formState =>
              props.userUpdatedForm({
                ...props.form,
                ...formState,
              })
            }
          />
          {/* Sections */}
          <StudySectionCards
            isLoading={props.isSaving}
            isEditMode={true}
            moduleForm={props.moduleForm}
            sections={props.sections}
            userToggledModuleModal={props.userToggledModuleModal}
            userUpdatedModuleForm={props.userUpdatedModuleForm}
            userUpdatedSections={props.userUpdatedSections}
            userClickedEditModule={props.userClickedEditModule}
            userClickedDeleteModule={props.userClickedDeleteModule}
            userClickedDeleteSection={props.userClickedDeleteSection}
            userClickedAddViaModal={props.userClickedAddContentViaModal}
          />
        </RhFormGridColumn>
        <RhFormGridColumn>
          {/* Publishing Status */}
          <PublishingStatusCard
            publishingStatusValue={props.form.publishingStatus || null}
            onPublishingStatusChange={selectedOption => {
              props.userUpdatedForm({
                ...props.form,
                publishingStatus: selectedOption.value,
              });
            }}
          />
          {/* Category */}
          <CategoryCard
            initialValue={props.form.categories}
            onChange={categories => {
              props.userUpdatedForm({
                ...props.form,
                categories,
              });
            }}
          />
          {/* TIme to Complete */}
          {/* <TimeToCompleteCard
            hours={props.form.hours}
            minutes={props.form.minutes}
            onChange={(hours, minutes) => {
              props.userUpdatedForm({
                ...props.form,
                hours,
                minutes,
              });
            }}
          /> */}
          {/* Feature Image */}
          <FeatureImageCard
            value={props.form.featureImage}
            valueInUrl={props.form.featureImageUrl}
            onChange={value => {
              props.userUpdatedForm({
                ...props.form,
                featureImage: value,
              });
            }}
            onRemove={() => {
              props.userUpdatedForm({
                ...props.form,
                featureImage: undefined,
              });
            }}
          />
          {/* Introduction Video */}
          <IntroductionVideoCard
            form={{ introVideo: props.form.introVideo }}
            isLoadingIntroductionVideo={props.isLoadingIntroductionVideo}
            userClickedDelete={() =>
              props.userUpdatedForm({
                ...props.form,
                introVideo: undefined,
              })
            }
            userUpdatedForm={formState =>
              props.userUpdatedForm({
                ...props.form,
                ...formState,
              })
            }
          />
          {/* Tags */}
          <TagsCard
            form={props.form}
            userPushedToTags={props.userPushedToTags}
            userUpdatedForm={formState =>
              props.userUpdatedForm({
                ...props.form,
                ...formState,
              })
            }
          />
          {/* Taxonomic Category */}
          {/* TODO: add taxonomic category if API is updated */}
          {/* <TaxonomicCategoryCard /> */}

          {/* Summary of content map from guided content */}
          {!!props.form.contentMaps.length && (
            <GuidedContentCard contentMaps={props.form.contentMaps} onEdit={props.userClickedEditContentMap} />
          )}
        </RhFormGridColumn>
      </RhFormGrid>

      {/* MODULE MODAL */}
      <ModuleModal
        isModuleModalOpen={props.isModuleModalOpen}
        isModuleFormFileUploading={props.isModuleFormFileUploading}
        isLoading={props.isModuleFormLoading}
        moduleForm={props.moduleForm}
        moduleFormError={props.moduleFormError}
        fileInputKeys={props.fileInputKeys}
        userChoseModuleFile={props.userChoseModuleFile}
        userResetModuleForm={props.userResetModuleForm}
        userSubmittedModuleForm={props.userSubmittedModuleForm}
        userToggledModuleModal={props.userToggledModuleModal}
        userUpdatedModuleForm={props.userUpdatedModuleForm}
        userClickedDeleteModuleFormFile={props.userClickedDeleteModuleFormFile}
      />

      {/* Jira-like Content Modal */}
      <JiralikeContentModal
        isOpen={props.isContentFormModalOpen}
        isFileUploading={props.isModuleFormFileUploading}
        isLoading={props.isModuleFormLoading}
        moduleForm={props.moduleForm}
        moduleFormError={props.moduleFormError}
        fileInputKeys={props.fileInputKeys}
        weekTagOptions={props.weekTagOptions}
        dayTagOptions={props.dayTagOptions}
        userToggledModal={props.userClickedAddContentViaModal}
        userChoseModuleFile={props.userChoseModuleFile}
        userResetModuleForm={props.userResetModuleForm}
        userSubmittedModuleForm={props.userClickedSaveViaContentModal}
        userUpdatedModuleForm={props.userUpdatedModuleForm}
        userClickedDeleteModuleFormFile={props.userClickedDeleteModuleFormFile}
      />

      {/* Guided Content Creation Modal */}
      <GuidedContentCreationModal
        initial={props.form.contentMaps}
        isOpen={props.isGuidedModalOpen}
        onCancel={props.userClickedCancelonGuidedModal}
        onSubmit={props.userClickedSubmitonGuidedModal}
      />
    </DashboardWrapper>
  );
};
